import React from 'react';
import { keyframes, styled } from '@mui/material';
import { PieArcDatum } from 'd3';
import { DatumId, WithDataLabels } from '../../core/Chart.types';
import { computeRingArc } from './PieSlice.utils';
import { PolylineLabel, polylineLabelClassNames } from './PolylineLabel';
import { TagLabel, tagLabelClassNames } from './TagLabel';

export const pieSliceClassNames = {
  arc: 'Chart-PieArc',
  ring: 'Chart-PieRing',
} as const;

const PIE_TRANSITION_DURATION = '220ms';
const PIE_TRANSITION_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

const PIE_LABEL_FADE_IN = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

export type SliceProps<Datum> = WithDataLabels & {
  arcPath: string;
  baseRadius: number;
  index: number;
  highlighted: boolean;
  selected: boolean;
  isInteractive: boolean;
  color: string;
  label?: string | number;
  datumId: DatumId;
  total?: number;
  datum: Datum;
  polylineScale?: number;
} & PieArcDatum<Datum> &
  React.ComponentProps<'g'>;

export const PieSlice = <Datum,>({
  startAngle,
  endAngle,
  padAngle,
  arcPath,
  highlighted,
  selected,
  isInteractive,
  dataLabelMode,
  index,
  color,
  label,
  datumId,
  baseRadius,
  total,
  datum,
  polylineScale = 1,
  onPointerEnter,
  onPointerLeave,
  onClick,
  ref,
  ...groupProps
}: SliceProps<Datum>) => {
  const pieArc = { startAngle, endAngle, padAngle };
  const { ringArc, ringOuterRadius, ringInnerRadius } = computeRingArc(pieArc, baseRadius, selected);

  return (
    <SliceGroup
      role="listitem"
      data-selected={selected || undefined}
      data-highlighted={highlighted || undefined}
      data-interactive={isInteractive ? '' : undefined}
      data-index={index}
      data-datum-id={datumId}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      ref={ref}
      {...groupProps}>
      <path data-index={index} className={pieSliceClassNames.arc} d={arcPath} fill={color} stroke="white" />
      {ringArc && <path aria-hidden="true" className={pieSliceClassNames.ring} d={ringArc} fill={color} />}
      {dataLabelMode === 'polyline' && (
        <PolylineLabel
          datum={datum}
          angles={pieArc}
          ringInnerRadius={ringInnerRadius}
          ringOuterRadius={ringOuterRadius}
          highlighted={highlighted}
          selected={selected}
          color={color}
          total={total}
          scale={polylineScale}
        />
      )}
      {label && dataLabelMode === 'tag' && (
        <TagLabel
          label={label}
          angles={pieArc}
          ringInnerRadius={ringInnerRadius}
          ringOuterRadius={ringOuterRadius}
          highlighted={highlighted}
          selected={selected}
          color={color}
        />
      )}
    </SliceGroup>
  );
};

const SliceGroup = styled('g')(({ theme }) => ({
  [`& .${pieSliceClassNames.arc}`]: {
    cursor: 'default',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    transition: theme.transitions.create(['opacity', 'filter', 'transform', 'd'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
  },
  [`&[data-interactive] .${pieSliceClassNames.arc}`]: {
    cursor: 'pointer',
  },
  [`&[data-selected] .${pieSliceClassNames.arc}`]: {
    transform: 'scale(1.015)',
  },
  [`&[data-highlighted] .${pieSliceClassNames.arc}`]: {
    filter: 'brightness(1.05)',
  },

  [`g[data-has-selection] &:not([data-selected]) .${pieSliceClassNames.arc}`]: {
    opacity: 0.15,
  },

  [`& .${pieSliceClassNames.ring}`]: {
    pointerEvents: 'none',
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'd'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
  },
  [`&[data-highlighted] .${pieSliceClassNames.ring}`]: {
    opacity: 0.5,
  },
  [`g[data-has-selection] &[data-highlighted]:not([data-selected]) .${pieSliceClassNames.ring}`]: {
    opacity: 0.05,
  },

  [`& .${polylineLabelClassNames.line}, & .${polylineLabelClassNames.label}`]: {
    pointerEvents: 'none',
  },
  [`& .${polylineLabelClassNames.line}`]: {
    transition: theme.transitions.create(['opacity', 'd'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
    animation: `${PIE_LABEL_FADE_IN} ${PIE_TRANSITION_DURATION} ${PIE_TRANSITION_EASING}`,
  },
  [`& .${polylineLabelClassNames.label}`]: {
    userSelect: 'none',
    fill: theme.palette.colors.neutrals[600],
    transition: theme.transitions.create(['opacity', 'x', 'y'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
    animation: `${PIE_LABEL_FADE_IN} ${PIE_TRANSITION_DURATION} ${PIE_TRANSITION_EASING}`,
  },

  [`& .${tagLabelClassNames.root}`]: {
    pointerEvents: 'none',
    transition: theme.transitions.create(['opacity', 'x', 'y'], {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
    animation: `${PIE_LABEL_FADE_IN} ${PIE_TRANSITION_DURATION} ${PIE_TRANSITION_EASING}`,
  },
  [`& .${tagLabelClassNames.content}`]: {
    transition: theme.transitions.create('transform', {
      duration: PIE_TRANSITION_DURATION,
      easing: PIE_TRANSITION_EASING,
    }),
  },

  [`g[data-has-selection] &:not([data-selected]) .${polylineLabelClassNames.line},
    g[data-has-selection] &:not([data-selected]) .${polylineLabelClassNames.label},
    g[data-has-selection] &:not([data-selected]) .${tagLabelClassNames.root}`]: {
    opacity: 0.25,
  },
}));
